/*
 * Rollups & Indicators — workspace + platform view of indicator
 * definitions, their preview values, and which buckets feed them.
 * Mirror of the embedded-app indicator surface but rendered inside the
 * web consoles so analysts don't have to round-trip through DHIS2.
 *
 * Preview is cheap (computed in the API per-call against in-memory
 * buckets) so we lazy-load values for the focused indicator only.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { api, ApiError } from "../lib/api.js";
import { SkeletonTable } from "../components/Skeleton.js";
import LivePanel from "../components/LivePanel.js";
import { useLiveEvents } from "../lib/useLiveEvents.js";

type Indicator = Awaited<ReturnType<typeof api.listIndicators>>["indicators"][number];
type Preview = Awaited<ReturnType<typeof api.previewIndicator>>;

export interface RollupsIndicatorsProps {
  scope: "workspace" | "platform";
}

export default function RollupsIndicators({ scope }: RollupsIndicatorsProps) {
  const [indicators, setIndicators] = useState<Indicator[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [previews, setPreviews] = useState<Map<string, Preview>>(new Map());
  const [loadingPreviewId, setLoadingPreviewId] = useState<string | null>(null);
  const [pulsedIds, setPulsedIds] = useState<Set<string>>(new Set());

  const fetchIndicators = () => {
    api.listIndicators()
      .then((r) => setIndicators(r.indicators))
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  };
  useEffect(() => {
    let cancelled = false;
    api.listIndicators()
      .then((r) => { if (!cancelled) setIndicators(r.indicators); })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, []);

  // Live wiring — bucket changes (tally / data) and indicator events
  // both trigger a refresh + re-preview of any indicator the user has
  // already loaded a preview for. This way the preview values stay
  // honest after a fresh rollup or capture submission lands.
  const live = useLiveEvents({
    scope: scope === "platform" ? "platform" : undefined,
    categories: ["indicator", "tally", "aggregate", "trk-data", "agg-data"],
    tailMax: 50
  });
  const liveCounters = live.countersIn(5 * 60 * 1000);
  const liveTone = live.connectionState === "open" ? "ok" : live.connectionState === "connecting" ? "info" : "err";
  const liveLabel = live.connectionState === "open"
    ? `● Live · ${liveCounters.total} events / 5m`
    : live.connectionState === "connecting" ? "Connecting…" : "Offline";

  const debouncedRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (live.events.length === 0) return;
    if (debouncedRef.current) clearTimeout(debouncedRef.current);
    debouncedRef.current = setTimeout(async () => {
      fetchIndicators();
      // Re-preview every indicator we already have a preview for so the
      // value column stays accurate as buckets change.
      const ids = [...previews.keys()];
      for (const id of ids) {
        try {
          const p = await api.previewIndicator(id);
          setPreviews((m) => {
            const prev = m.get(id);
            const next = new Map(m).set(id, p);
            if (prev && prev.value !== p.value) {
              setPulsedIds((s) => { const ns = new Set(s); ns.add(id); return ns; });
              setTimeout(() => setPulsedIds((s) => { const ns = new Set(s); ns.delete(id); return ns; }), 1800);
            }
            return next;
          });
        } catch { /* skip — refresh will pick it up next time */ }
      }
    }, 1500);
    return () => { if (debouncedRef.current) clearTimeout(debouncedRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live.events[0]?.id]);

  const filtered = useMemo(() => {
    if (!indicators) return [];
    if (!search.trim()) return indicators;
    const q = search.toLowerCase();
    return indicators.filter((i) =>
      i.name.toLowerCase().includes(q) ||
      i.id.toLowerCase().includes(q) ||
      i.numeratorMetricId.toLowerCase().includes(q) ||
      (i.denominatorMetricId ?? "").toLowerCase().includes(q)
    );
  }, [indicators, search]);

  async function loadPreview(id: string) {
    setLoadingPreviewId(id);
    try {
      const p = await api.previewIndicator(id);
      setPreviews((m) => new Map(m).set(id, p));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoadingPreviewId(null);
    }
  }

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>
            Rollups &amp; indicators
            <span className={`pill pill-${liveTone}`} style={{ fontSize: 11, marginLeft: 12, verticalAlign: "middle" }}
                  title={`Connection: ${live.connectionState}`}>
              {liveLabel}
            </span>
          </h1>
          <p className="muted">
            Indicator definitions and their computed preview values.
            Loaded previews refresh themselves when rollup / tally / data events fire — values pulse green when they change.
          </p>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}

      <section className="transfer-filters" aria-label="Indicator filters">
        <label className="transfer-filter transfer-filter-grow">
          Search
          <input type="search" placeholder="name / id / metric…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </section>

      <LivePanel
        title="Live indicator + bucket activity"
        categories={["indicator", "tally", "aggregate", "trk-data", "agg-data"]}
        tailMax={20}
      />

      {indicators === null && <SkeletonTable rows={5} columns={scope === "platform" ? 7 : 6} />}
      {indicators !== null && indicators.length === 0 && (
        <div className="empty-state-card">
          <h2 style={{ marginTop: 0 }}>No indicators defined yet</h2>
          <p>
            Indicators combine TallyMetrics into ratios, sums, and rates. To create your first indicator:
          </p>
          <ol>
            <li>Make sure tracker metadata is synced — TallyMetrics need to exist first.</li>
            <li>Open the embedded app's <strong>Indicator studio</strong> and pick a numerator + optional denominator.</li>
            <li>Publish the indicator — it will appear here with a computed preview value.</li>
          </ol>
        </div>
      )}

      {filtered.length > 0 && (
        <table className="transfer-table">
          <thead>
            <tr>
              <th>Indicator</th>
              <th>Numerator</th>
              <th>Denominator</th>
              {scope === "platform" && <th>Workspace</th>}
              <th>Status</th>
              <th style={{ textAlign: "right" }}>Preview</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((ind) => {
              const preview = previews.get(ind.id);
              const pulsed = pulsedIds.has(ind.id);
              return (
                <tr key={ind.id} className={pulsed ? "row-pulsed" : undefined}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{ind.name}</div>
                    <div className="mono" style={{ fontSize: 11, color: "var(--text-muted)" }}>{ind.id}</div>
                  </td>
                  <td className="mono" style={{ fontSize: 11 }}>{ind.numeratorMetricId}</td>
                  <td className="mono" style={{ fontSize: 11 }}>{ind.denominatorMetricId ?? "—"}</td>
                  {scope === "platform" && <td className="mono" style={{ fontSize: 11 }}>{ind.workspaceId ?? "—"}</td>}
                  <td>
                    {ind.published
                      ? <span className="pill pill-ok">published</span>
                      : <span className="pill pill-muted">draft</span>}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {preview
                      ? (preview.valid
                          ? Number.isFinite(preview.value) ? preview.value.toFixed(3) : preview.value
                          : <span className="pill pill-err" title={preview.diagnostics.join("; ")}>invalid</span>)
                      : <span className="muted">—</span>}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => loadPreview(ind.id)}
                      disabled={loadingPreviewId === ind.id}
                    >
                      {loadingPreviewId === ind.id ? "Loading…" : preview ? "Refresh" : "Preview"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
